/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

import ListItemLink from './ListItemLink'
import CustomIcons from '../../../icons/CustomIcons'

import { ApiReducer, ApplicationState, CredentialsReducer, UserReducer } from '../../../data/interfaces';
import { PATH_SEPARATOR } from '../../../data/constants';
import {
    trackAPIContractsNavBarClick,
    trackAPICredentialsNavBarClick,
    trackAPIDocumentationNavBarClick, trackAPISDKNavBarClick
} from "../../../util/app-insights";
import {
    isContractsAccessible,
    isCredentialMenuAccessible,
    isGraphqlApi,
    isProductionKeyGenerationAccessible,
    isSandboxKeyGenerationAccessible
} from '../../../data/utils';
import { List, makeStyles } from '@material-ui/core';
import Settings from '../../../conf/settings';
import InfoTooltip from '../../../elements/CustomTooltip/InfoTooltip';
import { checkCredentials } from '../../../data/operations';

const useStyles = makeStyles((theme) => ({
    nested: {
        paddingLeft: theme.spacing(3.75),
    },
    nestedItem: {
        marginTop: -theme.spacing(3.5),
        marginBottom: -theme.spacing(3.5)
    }
}));

export interface ApiCredentialUrlParams {
    env?: string;
    page: string;
}

interface ApiUrlParams {
    page: string;
}

interface MainMenuItemsProps {
    organizationHandle: string,
    apiReducer: ApiReducer,
    userReducer: UserReducer,
    credentialsReducer: CredentialsReducer,
    dispatchCheckCredentials: () => void
}

function MainMenuItems(props: MainMenuItemsProps) {
    const intl = useIntl();
    const location = useLocation();
    const classes = useStyles();
    const { api } = props.apiReducer;
    const isApimTokenObtained = props.userReducer.apimTokenObtained;
    const allowedScopes = props.userReducer.user.allowedScopes;
    const selectedOrgHandleName = props.organizationHandle;
    const apiId = api ? api.id : ':apiId';
    const [showOAuthCredentials, setShowOAuthCredentials] = React.useState(false);
    const {isKeyFetching, keys} = props.credentialsReducer;

    const isApiKeyEnabledApi = api?.securityScheme?.includes('api_key') ?? false;  

    const isTabSelected = (page: string, credentialEnv?: string): boolean => {
        const currentPath = location.pathname;
        if (credentialEnv) {
            const { params } = matchPath<ApiCredentialUrlParams>(currentPath, "/:orgName/apis/:apiId/:page/:env") || {};
            return !!params && params.env === credentialEnv && params.page === page;
        }
        const { params } = matchPath<ApiUrlParams>(currentPath, "/:orgName/apis/:apiId/:page") || {};
        return !!params && params.page === page;
    }

    const menuItemsI18n = {
        overview: intl.formatMessage({
            id: 'api.menuItems.overview',
            defaultMessage: 'Overview'
        }),
        credentials: intl.formatMessage({
            id: 'api.menuItems.credentials',
            defaultMessage: 'Credentials'
        }),
        productionCredentials: intl.formatMessage({
            id: 'api.menuItems.credentials.productionKeys',
            defaultMessage: 'Production'
        }),
        sandboxCredentials: intl.formatMessage({
            id: 'api.menuItems.credentials.sandboxKeys',
            defaultMessage: 'Sandbox'
        }),
        tryout: intl.formatMessage({
            id: 'api.menuItems.tryout',
            defaultMessage: 'Try Out'
        }),
        resources: intl.formatMessage({
            id: 'api.menuItems.resources',
            defaultMessage: 'Resources'
        }),
        documentation: intl.formatMessage({
            id: 'api.menuItems.documentation',
            defaultMessage: 'Documentation'
        }),
        sdks: intl.formatMessage({
            id: 'api.menuItems.sdks',
            defaultMessage: 'SDKs'
        }),
        contracts: intl.formatMessage({
            id: 'api.menuItems.contracts',
            defaultMessage: 'Contracts'
        })
    };

    useEffect(() => {
        if (Settings.apiTryoutTestKeyEnabled && api?.id) {
            props.dispatchCheckCredentials();
        }
    }, [api]);

    useEffect(() => {
        if (Settings.apiTryoutTestKeyEnabled && !isKeyFetching && keys?.list && keys.list.length > 0) {
            setShowOAuthCredentials(true);
        }
    }, [keys, isKeyFetching]);

    return (
        <div>
            <ListItemLink
                to={PATH_SEPARATOR + selectedOrgHandleName + "/apis/" + apiId + "/overview"}
                primary={menuItemsI18n.overview}
                icon={<CustomIcons icon={isTabSelected('overview') ? 'overview-selected' : 'overview'} />}
                selected={isTabSelected('overview')}
                id='overview-item-link'
            />
            {(isApimTokenObtained && isCredentialMenuAccessible(allowedScopes)) && (!Settings.apiTryoutTestKeyEnabled || isApiKeyEnabledApi || showOAuthCredentials) && (
                <>
                    <InfoTooltip
                        content={intl.formatMessage({
                            id: 'api.menuItems.credentials.permission.denied.tooltip',
                            defaultMessage: "You don't have permission to view or generate credentials"
                        })}
                        darken={true}
                        isDisabled={isCredentialMenuAccessible(allowedScopes)}
                    >
                        <span>
                            <ListItemLink
                                to={PATH_SEPARATOR + selectedOrgHandleName + "/apis/" + apiId + "/credentials/" +
                                    (Settings.sandboxKeyGenerationEnabled ? "sandbox" : "production")}
                                primary={menuItemsI18n.credentials}
                                icon={<CustomIcons icon={isTabSelected('credentials') ? 'credentials-selected' : 'credentials'} />}
                                selected={isTabSelected('credentials')}
                                id='credentials-item-link'
                                onclick={trackAPICredentialsNavBarClick}
                            />
                        </span>
                    </InfoTooltip>
                    <List component="div" className={classes.nested}>
                        {Settings.sandboxKeyGenerationEnabled && (
                            <div className={classes.nestedItem} data-testid="li-oauth-key-wrapper">
                                <InfoTooltip
                                    content={intl.formatMessage({
                                        id: 'api.menuItems.credentials.sandboxKeys.permission.denied.tooltip',
                                        defaultMessage: "You don't have permission to view or generate sandbox credentials"
                                    })}
                                    darken={true}
                                    isDisabled={isSandboxKeyGenerationAccessible(allowedScopes)}
                                >
                                    <span>
                                        <ListItemLink
                                            to={PATH_SEPARATOR + selectedOrgHandleName + "/apis/" + apiId + "/credentials/sandbox"}
                                            primary={menuItemsI18n.sandboxCredentials}
                                            selected={isTabSelected('credentials', 'sandbox')}
                                            id='sandbox-credentials-menu-item'
                                            disabled={!isSandboxKeyGenerationAccessible(allowedScopes)}
                                        />
                                    </span>
                                </InfoTooltip>
                            </div>
                        )}
                        <div className={classes.nestedItem} data-testid="li-oauth-key-wrapper">
                            <InfoTooltip
                                content={intl.formatMessage({
                                    id: 'api.menuItems.credentials.productionKeys.permission.denied.tooltip',
                                    defaultMessage: "You don't have permission to view or generate production credentials"
                                })}
                                darken={true}
                                isDisabled={isProductionKeyGenerationAccessible(allowedScopes)}
                            >
                                <span>
                                    <ListItemLink
                                        to={PATH_SEPARATOR + selectedOrgHandleName + "/apis/" + apiId + "/credentials/production"}
                                        primary={menuItemsI18n.productionCredentials}
                                        selected={isTabSelected('credentials', 'production')}
                                        id='production-credentials-menu-item'
                                        disabled={!isProductionKeyGenerationAccessible(allowedScopes)}
                                    />
                                </span>
                            </InfoTooltip>
                        </div>
                    </List>
                </>
            )}
            {isApimTokenObtained &&
            <ListItemLink
                to={PATH_SEPARATOR + selectedOrgHandleName + "/apis/" + apiId + "/tryout"}
                primary={menuItemsI18n.tryout}
                icon={<CustomIcons icon={isTabSelected('tryout') ? 'tryout-selected' : 'tryout'} />}
                selected={isTabSelected('tryout')}
                id='tryout-item-link'
            />
            }
            {!isApimTokenObtained &&
            <ListItemLink
                to={PATH_SEPARATOR + selectedOrgHandleName + "/apis/" + apiId + "/resources"}
                primary={menuItemsI18n.resources}
                icon={<CustomIcons icon={isTabSelected('resources') ? 'tryout-selected' : 'tryout'}/>}
                selected={isTabSelected('resources')}
                id='resources-item-link'
                disabled={isGraphqlApi(api)}
            />
            }
            <ListItemLink
                to={PATH_SEPARATOR + selectedOrgHandleName + "/apis/" + apiId + "/documents"}
                primary={menuItemsI18n.documentation}
                icon={<CustomIcons icon={isTabSelected('documents') ? 'documentation-selected' : 'documentation'} />}
                selected={isTabSelected('documents')}
                id='documents-item-link'
                onclick={trackAPIDocumentationNavBarClick}
            />
            {(!isGraphqlApi(api) && isApimTokenObtained) && (
                <ListItemLink
                    to={PATH_SEPARATOR + selectedOrgHandleName + "/apis/" + apiId + "/sdks"}
                    primary={menuItemsI18n.sdks}
                    icon={<CustomIcons icon={isTabSelected('sdks') ? 'sdks-selected' : 'sdks'} />}
                    selected={isTabSelected('sdks')}
                    id='sdks-item-link'
                    onclick={trackAPISDKNavBarClick}
                />
            )}
            {(isApimTokenObtained && isContractsAccessible(allowedScopes)) && (
                <InfoTooltip
                    content={intl.formatMessage({
                        id: 'api.menuItems.contaracts.permission.denied.tooltip',
                        defaultMessage: "You don't have permission to view contracts"
                    })}
                    darken={true}
                    isDisabled={isContractsAccessible(allowedScopes)}
                >
                    <span>
                        <ListItemLink
                            to={PATH_SEPARATOR + selectedOrgHandleName + "/apis/" + apiId + "/contracts"}
                            primary={menuItemsI18n.contracts}
                            icon={<CustomIcons icon={isTabSelected('contracts') ? 'contracts-selected' : 'contracts'} />}
                            selected={isTabSelected('contracts')}
                            id='contracts-item-link'
                            onclick={trackAPIContractsNavBarClick}
                        />
                    </span>
                </InfoTooltip>
            )}
        </div>
    )
}

const mapDispatchToProps = {
    dispatchCheckCredentials: checkCredentials,
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiReducer: state.apiReducer,
        userReducer: state.userReducer,
        credentialsReducer: state.credentialsReducer
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(MainMenuItems);
